<template>
  <div>
    <h2 class="text-h5 mb-4">Синхронизация с Active Directory</h2>
    <p class="mb-4 grey--text">
      Подключитесь к серверу Active Directory для получения и обновления пользователей из группы "Пользователь домена".
      <br>
      <small>
        <strong>Примечание:</strong> Настройки можно сохранить в переменных окружения:
        <code>AD_SERVER_ADDRESS</code>, <code>AD_USERNAME</code>, <code>AD_PASSWORD</code>, <code>AD_GROUP_NAME</code>.
        Если настройки сохранены, они будут автоматически загружены в форму.
      </small>
    </p>

    <v-progress-linear
      v-if="loadingSettings"
      indeterminate
      color="primary"
      class="mb-4"
    ></v-progress-linear>

    <v-alert
      v-if="!loadingSettings && (form.serverAddress || form.username || passwordSetInEnv)"
      type="info"
      dense
      class="mb-4"
      dismissible
    >
      <small>
        <strong>Настройки загружены из переменных окружения.</strong>
        Вы можете изменить их в форме или оставить как есть для использования значений из .env файла.
      </small>
    </v-alert>

    <v-form ref="form" @submit.prevent="handleSync">
      <v-text-field
        v-model="form.serverAddress"
        label="Адрес сервера"
        placeholder="ldap://domain.com или ldap://192.168.1.1:389 (можно оставить пустым, если сохранено в настройках)"
        outlined
        class="mb-3"
      ></v-text-field>

      <v-text-field
        v-model="form.username"
        label="Логин"
        placeholder="DOMAIN\\user или user@domain.com (можно оставить пустым, если сохранено в настройках)"
        outlined
        class="mb-3"
      ></v-text-field>

      <v-text-field
        v-model="form.password"
        label="Пароль"
        type="password"
        :placeholder="passwordPlaceholder"
        :hint="passwordHint"
        persistent-hint
        outlined
        class="mb-3"
      ></v-text-field>

      <v-text-field
        v-model="form.groupName"
        label="Имя группы"
        placeholder="Пользователь домена (оставьте пустым для синхронизации всех пользователей)"
        hint="Группа в AD для фильтрации пользователей. Оставьте пустым для синхронизации всех пользователей домена."
        persistent-hint
        outlined
        class="mb-3"
      ></v-text-field>

      <div class="d-flex gap-2">
        <v-btn
          color="info"
          large
          :loading="testing"
          @click="handleTestConnection"
          class="mr-2"
        >
          Проверить подключение
        </v-btn>
        <v-btn
          color="primary"
          large
          :loading="syncing"
          @click="handleSync"
        >
          Синхронизировать
        </v-btn>
      </div>
    </v-form>

    <v-alert
      v-if="testResult"
      :type="testResult.success ? 'success' : 'error'"
      class="mt-4"
      dismissible
      @input="testResult = null"
    >
      <div v-if="testResult.success">
        <strong>Подключение успешно!</strong>
        <ul class="mt-2 mb-0">
          <li>Базовый DN: {{ testResult.domain_info?.base_dn || 'не определен' }}</li>
          <li>Всего пользователей в домене: {{ testResult.all_users_count || testResult.total_users || 0 }}</li>
        </ul>
        <div v-if="testResult.sample_users && testResult.sample_users.length > 0" class="mt-2">
          <strong>Примеры пользователей (первые 10):</strong>
          <ul class="mb-0">
            <li v-for="(user, index) in testResult.sample_users" :key="index">
              {{ user.username }} - {{ user.email || 'нет email' }} ({{ user.display_name || user.first_name + ' ' + user.last_name }})
            </li>
          </ul>
        </div>
      </div>
      <div v-else>
        <strong>Ошибка подключения:</strong> {{ testResult.error }}
      </div>
    </v-alert>

    <v-alert
      v-if="syncResult"
      :type="syncResult.success ? (syncResult.warning ? 'warning' : 'success') : 'error'"
      class="mt-4"
      dismissible
      @input="syncResult = null"
    >
      <div v-if="syncResult.success">
        <strong v-if="syncResult.warning">Синхронизация завершена с предупреждением!</strong>
        <strong v-else>Синхронизация завершена успешно!</strong>
        <ul class="mt-2 mb-0">
          <li>Найдено в AD: {{ syncResult.total_from_ad }}</li>
          <li>Создано новых: {{ syncResult.stats.created }}</li>
          <li>Обновлено: {{ syncResult.stats.updated }}</li>
          <li>Пропущено: {{ syncResult.stats.skipped }}</li>
        </ul>
        <div v-if="syncResult.warning" class="mt-2">
          <strong>Предупреждение:</strong> {{ syncResult.warning }}
          <br>
          <small>
            Возможно, группа не содержит активных пользователей с email, или пользователи не являются членами указанной группы.
            Попробуйте указать другое имя группы (например, "Domain Users" для английской версии AD) или оставьте поле пустым для синхронизации всех пользователей домена.
          </small>
        </div>
        <div v-if="syncResult.stats.errors && syncResult.stats.errors.length > 0" class="mt-2">
          <strong>Ошибки:</strong>
          <ul class="mb-0">
            <li v-for="(error, index) in syncResult.stats.errors" :key="index">{{ error }}</li>
          </ul>
        </div>
      </div>
      <div v-else>
        <strong>Ошибка синхронизации:</strong> {{ syncResult.error }}
        <div v-if="syncResult.error && syncResult.error.includes('не найдена')" class="mt-2">
          <small>
            <strong>Возможные решения:</strong>
            <ul class="mb-0">
              <li>Проверьте правильность имени группы (например, "Domain Users" для английской версии AD)</li>
              <li>Оставьте поле "Имя группы" пустым для синхронизации всех пользователей домена</li>
              <li>Убедитесь, что указанная группа существует в Active Directory</li>
            </ul>
          </small>
        </div>
      </div>
    </v-alert>
  </div>
</template>

<script>
import authApi from '@/services/authApi'

export default {
  name: 'ActiveDirectoryTab',
  data() {
    return {
      syncing: false,
      testing: false,
      loadingSettings: false,
      passwordSetInEnv: false,
      form: {
        serverAddress: '',
        username: '',
        password: '',
        groupName: 'Пользователь домена'
      },
      syncResult: null,
      testResult: null
    }
  },
  computed: {
    passwordPlaceholder() {
      if (this.passwordSetInEnv) {
        return 'Пароль задан в настройках (оставьте пустым для использования)'
      }
      return 'Можно оставить пустым, если сохранено в настройках'
    },
    passwordHint() {
      if (this.passwordSetInEnv) {
        return 'Пароль уже задан в переменных окружения. Оставьте поле пустым для использования сохраненного пароля, или введите новый для переопределения.'
      }
      return 'Если пароль сохранен в переменных окружения, поле можно оставить пустым'
    }
  },
  mounted() {
    this.loadSettings()
  },
  methods: {
    async loadSettings() {
      this.loadingSettings = true
      try {
        const response = await authApi.getActiveDirectorySettings()
        if (response && response.ok) {
          // Заполняем форму значениями из .env
          if (response.server_address) {
            this.form.serverAddress = response.server_address
          }
          if (response.username) {
            this.form.username = response.username
          }
          if (response.group_name) {
            this.form.groupName = response.group_name
          }
          // Пароль не возвращается, но показываем, что он задан
          this.passwordSetInEnv = response.password_set || false
        }
      } catch (error) {
        console.warn('Не удалось загрузить настройки AD из переменных окружения:', error)
        // Не показываем ошибку пользователю, так как настройки могут быть не заданы
      } finally {
        this.loadingSettings = false
      }
    },
    async handleTestConnection() {
      this.testing = true
      this.testResult = null

      try {
        const requestData = {}
        if (this.form.serverAddress) {
          requestData.server_address = this.form.serverAddress
        }
        if (this.form.username) {
          requestData.username = this.form.username
        }
        if (this.form.password) {
          requestData.password = this.form.password
        }

        const response = await authApi.testActiveDirectoryConnection(requestData)

        this.testResult = {
          success: true,
          ...response
        }
      } catch (error) {
        this.testResult = {
          success: false,
          error: error.response?.data?.detail || error.message || 'Ошибка подключения к Active Directory'
        }
      } finally {
        this.testing = false
      }
    },
    async handleSync() {

      this.syncing = true
      this.syncResult = null

      try {
        // Передаем только заполненные поля, пустые будут заменены значениями из переменных окружения
        const requestData = {}
        if (this.form.serverAddress) {
          requestData.server_address = this.form.serverAddress
        }
        if (this.form.username) {
          requestData.username = this.form.username
        }
        if (this.form.password) {
          requestData.password = this.form.password
        }
        if (this.form.groupName) {
          requestData.group_name = this.form.groupName
        }

        const response = await authApi.syncActiveDirectory(requestData)

        this.syncResult = {
          success: true,
          ...response
        }

        // Очищаем пароль после успешной синхронизации
        this.form.password = ''

        // Обновляем список пользователей в родительском компоненте
        this.$emit('sync-complete')
      } catch (error) {
        this.syncResult = {
          success: false,
          error: error.response?.data?.detail || error.message || 'Ошибка синхронизации с Active Directory'
        }
      } finally {
        this.syncing = false
      }
    }
  }
}
</script>
